import { Link2, Plus, Trash2 } from 'lucide-react';
import { globalSwatches } from '../core/global-swatches';
import type { EditorDocument } from '../core/model';
import { findLayer } from '../core/model';
import type { DocumentSession } from '../core/session';
import { runCommand } from '../semantic/commands/run';
import { COLOUR_TRACE_PRESETS } from '../vector/colour-stack-presets';
import { layerColourStack } from '../vector/colour-stack';
import type { VectorObjectSelection } from './types';

export function GlobalSwatchesPanel({ document, session, foreground, selection, onSelectSwatch }: {
  document: EditorDocument; session: DocumentSession; foreground: string; selection: VectorObjectSelection | null;
  onSelectSwatch?: (swatchId: string) => void;
}) {
  const swatches = globalSwatches(document);
  const layerId = selection?.layerId ?? document.activeLayerIds[0];
  // Colour count for artwork that already exists. The trace dialog has the same
  // control for a raster it is about to convert; this is the same idea for a
  // vector layer that arrived with its colours already fixed — an imported SVG,
  // most obviously, which has no palette tree because nothing clustered it.
  const vectorLayer = layerId ? findLayer(document.root, layerId) : undefined;
  const stack = vectorLayer?.kind === 'vector' ? layerColourStack(vectorLayer) : null;
  const total = stack?.entries.length ?? 0;
  const live = vectorLayer?.kind === 'vector'
    ? new Set(vectorLayer.vector.objects.flatMap((object) => (object.fill && object.fill.alpha > 0 ? [object.fill.color.toLowerCase()] : []))).size
    : 0;
  const reduce = (count: number) => {
    if (!vectorLayer || vectorLayer.kind !== 'vector') return;
    runCommand(session, 'vector.reduceColours', { layer: vectorLayer.id, colours: count });
  };
  return <div className="global-swatches">
    {total > 1 ? <div className="colour-count">
      <label className="field">Colours <span>
        <input type="range" min="1" max={total} value={Math.min(live || total, total)} aria-label="Colours in this artwork"
          data-testid="vector-colour-count" onChange={(event) => reduce(Number(event.target.value))} />
        <em>{live || total}</em>
      </span></label>
      <div className="colour-count-presets">
        {COLOUR_TRACE_PRESETS.filter((preset) => preset < total).map((preset) => (
          <button key={preset} type="button" data-colour-preset={preset} className={live === preset ? 'active' : ''} onClick={() => reduce(preset)}>{preset}</button>
        ))}
        {live < total ? <button type="button" title="Back to every colour" onClick={() => reduce(total)}>all</button> : null}
      </div>
      {/* Merging follows the stack, so a colour folds into what it sits on —
          what deleting that layer would reveal — rather than into whatever is
          perceptually nearest, which would flatten the form. */}
      <small>Colours merge into what they sit on.</small>
    </div> : null}
    <div className="global-swatches-heading"><span><Link2 size={11} /> Global</span><button type="button" title="Create linked swatch from foreground" onClick={() => runCommand(session, 'swatches.add', { color: foreground })}><Plus size={11} /></button></div>
    {swatches.length === 0 ? <small>Linked document colors update every object at once.</small> : <div className="global-swatch-list">
      {swatches.map((swatch) => <div key={swatch.id}>
        <input type="color" aria-label={`Edit global swatch ${swatch.name}`} value={swatch.color} onChange={(event) => runCommand(session, 'swatches.update', { swatch: swatch.id, color: event.target.value })} />
        {/* Clicking the name SELECTS everything wearing this colour; applying it
            to the current selection is the shifted action. Selecting is the one
            you reach for constantly on traced or imported artwork, where finding
            all ninety objects of one colour by hand is the whole problem. */}
        <button type="button" title={`Select everything using ${swatch.name} — hold Shift to apply it to the selection instead`}
          data-swatch-select={swatch.id}
          onClick={(event) => {
            if (event.shiftKey) { if (layerId) runCommand(session, 'swatches.apply', { swatch: swatch.id, layer: layerId, objects: selection?.objectIds ?? [] }); return; }
            onSelectSwatch?.(swatch.id);
          }}>{swatch.name}</button>
        <button type="button" title="Delete swatch" onClick={() => runCommand(session, 'swatches.delete', { swatch: swatch.id })}><Trash2 size={10} /></button>
      </div>)}
    </div>}
  </div>;
}
